import { AssignmentTurnedInOutlined, Close } from '@material-ui/icons';
import React from 'react';

import { useEscapeKey } from '../../../../utils/useEscapeKey';

interface EditPolicyPopupProps {
  /** Whether the popup is visible. */
  displayModal: boolean;
  /**
   * Short product label for the policy being edited, e.g. "CGL". Interpolated
   * into the heading.
   */
  productLabel: string;
  /** Dismiss without leaving the quote page. */
  handleClose: () => void;
  /** Confirmed: navigate back to the business info page. */
  handleProceed: () => void;
}

/**
 * Confirmation shown when the applicant clicks "Edit" on a quoted policy card.
 * Editing means returning to the application, so the copy warns that the
 * application has to be completed and resubmitted or the changes are lost.
 */
const EditPolicyPopup: React.FC<EditPolicyPopupProps> = ({
  displayModal,
  productLabel,
  handleClose,
  handleProceed
}) => {
  useEscapeKey(handleClose, displayModal);

  return (
    <div
      data-testid="edit-policy-popup"
      className={`${
        displayModal ? '' : 'hidden '
      }fixed w-screen h-screen top-0 left-0 bg-black bg-opacity-50 z-50`}
      onClick={handleClose}
    >
      <div className="flex h-screen justify-center overflow-auto">
        {/* Wider than the other quote popups: this copy runs to two sentences. */}
        <div
          className="relative max-w-2xl flex flex-col h-fit-content bg-white border-2 border-white rounded-lg self-center my-8 mx-2 md:mx-0 p-6 md:p-10 text-center z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <Close
            data-testid="edit-policy-popup-close"
            className="absolute top-2 right-2 cursor-pointer"
            onClick={handleClose}
          />
          <div className="flex justify-center mb-4">
            <AssignmentTurnedInOutlined style={{ fontSize: '4rem' }} />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Want to edit your {productLabel} policy?
          </h2>
          <p className="text-base md:text-lg leading-relaxed mb-8">
            By hitting proceed you&apos;ll be brought back to your application
            to review, and edit the previous answers. Once the desired changes
            have been made in the application, please complete and resubmit for
            review, otherwise any changes will be lost (not saved).
          </p>
          <button
            data-testid="edit-policy-popup-proceed"
            className="w-full bg-tertiary hover:bg-tertiary-dark border-tertiary-dark rounded-md uppercase pointer font-bold px-14 py-3 text-mobileFont md:text-xl text-white"
            onClick={handleProceed}
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPolicyPopup;
